/**
 * Small formatting helpers shared across admin pages. Ported from the relTime /
 * value-render logic that lived (duplicated) in each public/admin/*.js boot
 * script — consolidated into one module so the pages don't re-implement it.
 * Pure functions, no DOM, no innerHTML (React renders text nodes, so these are
 * XSS-safe by construction — rebuild-lessons.md #1).
 */

/** Relative "Ns ago" / "Nm ago" string. Returns "—" for null/empty. */
export function relTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return String(iso);
  const diffMs = Date.now() - ts;
  if (diffMs < 0) return 'just now';
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** Absolute local timestamp, or "—" for null. */
export function absTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
}

/** Milliseconds → human duration ("420ms", "3.2s", "1m 5s"). */
export function duration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const mins = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  return `${mins}m ${rem}s`;
}

/** USD with 2dp, or "—" for null. */
export function usd(n: number | null | undefined): string {
  if (n == null) return '—';
  return `$${n.toFixed(2)}`;
}

/** A compact, readable rendering of a feature-flag value of any JSON type. */
export function renderFlagValue(value: unknown): string {
  if (value === true) return 'true';
  if (value === false) return 'false';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return `"${value}"`;
  const json = JSON.stringify(value);
  return json.length > 60 ? `${json.slice(0, 57)}…` : json;
}

/** Type guard: did a degradable section come back as a PENDING stub? */
export function isPendingSection(
  section: unknown,
): section is { status: 'PENDING'; error?: string; note?: string } {
  return (
    typeof section === 'object' &&
    section !== null &&
    (section as { status?: string }).status === 'PENDING'
  );
}

/** Type guard: did a system-info section come back as an `{ error }` stub? */
export function isErrorSection(section: unknown): section is { error: string } {
  return (
    typeof section === 'object' &&
    section !== null &&
    typeof (section as { error?: unknown }).error === 'string'
  );
}
